// Set up a phone or tablet — BRAT install, QR pairing from a signed-in
// desktop, then the in-plugin vault picker. Sourced from
// decisions/99-mobile-support.md (§Distribution: mobile builds ship via BRAT
// from GitHub releases) and specs/cli.md §6 (mobile carries the setup
// gestures the CLI owns on desktop).

import {
  IconDeviceMobile,
  IconDownload,
  IconFolders,
  IconLogout,
  IconQrcode,
} from "@tabler/icons-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DocSection, K, P, Points } from "../doc-ui";

export function MobileSetupDoc() {
  return (
    <>
      <P>
        There's no <K>cairn</K> CLI on a phone, so mobile setup works differently: you install the
        plugin with BRAT, pair the phone from a device that's already signed in, then pick a vault
        to sync inside the plugin.
      </P>

      <DocSection
        icon={IconDownload}
        title="1. Install the plugin (via BRAT)"
        description="On mobile the plugin is installed through BRAT, Obsidian's beta-plugin installer — it isn't in the community store yet."
      >
        <Points>
          <li>Install Obsidian from the App Store / Play Store and open (or create) a vault.</li>
          <li>
            In <K>Settings → Community plugins</K>, turn on community plugins, then browse for and
            install <strong>BRAT</strong> and enable it.
          </li>
          <li>
            The <K>aurum-ai/cairn</K> repository is private, so BRAT needs a GitHub personal
            access token to fetch releases. On github.com, go to{" "}
            <K>Settings → Developer settings → Fine-grained personal access tokens</K> and create
            one scoped as tightly as possible: repository access limited to{" "}
            <strong>only aurum-ai/cairn</strong>, with the single repository permission{" "}
            <strong>Contents: Read-only</strong> (that covers releases and their assets), and an
            expiry. Then, in BRAT's settings under the personal-access-token section, add an
            entry: any name you like for the <K>ID</K>, and the token itself in the{" "}
            <K>Secret</K> field.
          </li>
          <li>
            In BRAT's settings, choose <K>Add beta plugin</K>, enter the repository{" "}
            <K>aurum-ai/cairn</K>, pick the latest release, and add it.
          </li>
          <li>
            Back in <K>Community plugins</K>, make sure <strong>Cairn</strong> is enabled.
          </li>
        </Points>
        <Alert>
          <IconDownload />
          <AlertTitle>Don't use a classic token</AlertTitle>
          <AlertDescription>
            A classic PAT's <K>repo</K> scope grants read/write to every repository your account
            can reach, and it sits on your phone in plain text. A fine-grained token restricted to
            this one repo, read-only, is all BRAT needs.
          </AlertDescription>
        </Alert>
        <Alert>
          <IconDownload />
          <AlertTitle>Desktop never needs BRAT</AlertTitle>
          <AlertDescription>
            On a computer, <K>cairn vault clone</K> installs and configures the plugin from your
            own server — BRAT is only the mobile install path.
          </AlertDescription>
        </Alert>
      </DocSection>

      <DocSection
        icon={IconQrcode}
        title="2. Pair from a signed-in device"
        description="Pairing hands the phone a session — no password typing on a phone keyboard."
      >
        <Points>
          <li>
            On a desktop that's already signed in, open{" "}
            <K>Settings → Cairn → Show pairing code</K> in Obsidian. It generates a single-use
            pairing link and QR code that expires in 2 minutes.
          </li>
          <li>
            On the phone, scan the QR code — or open the <K>obsidian://cairn</K> link, or paste it
            into the pairing field in the Cairn plugin settings.
          </li>
          <li>The phone confirms and signs in. Each pairing is its own device in your session
            list, so you can revoke a phone without touching your other devices.</li>
        </Points>
        <Alert>
          <IconDeviceMobile />
          <AlertTitle>Pairing links redeem on mobile only</AlertTitle>
          <AlertDescription>
            On desktop, sign in with <K>cairn login</K> instead — a desktop click on a pairing link
            explains this rather than redeeming.
          </AlertDescription>
        </Alert>
      </DocSection>

      <DocSection
        icon={IconFolders}
        title="3. Choose a vault to sync"
        description="The paired phone is signed in but syncing nothing until you bind a vault."
      >
        <Points>
          <li>
            In the plugin settings, tap <K>Choose a vault to sync</K> and pick one of the vaults
            your account can access. The current folder starts pulling that vault's content.
          </li>
          <li>
            Mobile binds into an <em>empty</em> folder only (a folder with just{" "}
            <K>.obsidian/</K>-style config still counts as empty). A folder that already has notes
            is refused — join it from the desktop CLI instead, which safely previews and backs up
            the merge.
          </li>
          <li>
            You can't create vaults from a phone — create them with the CLI or the web app, then
            pick them up on mobile.
          </li>
        </Points>
      </DocSection>

      <DocSection
        icon={IconLogout}
        title="Signing out on mobile"
        description="Mobile sessions are per-vault, so logout lives in the plugin there."
      >
        <P>
          Use <K>Log out on this device</K> at the bottom of the plugin's Account section. It ends
          just that phone's session — your desktop login and other devices are untouched. You can
          also revoke any device from the web app under Account → Devices &amp; sessions.
        </P>
      </DocSection>
    </>
  );
}
